import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { MAX_FRESHDESK_RATING } from '../constants';
import { FormattedMessage, useIntl } from 'react-intl';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import type { RatingDistributionPoint, RatingTrendPoint } from '../types';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import { Box, Paper, Stack, Skeleton, useTheme, Typography, Grid2 as Grid } from '@mui/material';

type FeedbackChartsProps = {
  distribution: RatingDistributionPoint[];
  trend: RatingTrendPoint[];
  loading: boolean;
};

type ChartCardProps = {
  titleId: string;
  subtitleId: string;
  icon: React.ReactNode;
  children: React.ReactNode;
};

type ChartLegendItem = {
  color: string;
  label: string;
};

const ChartCard = ({ titleId, subtitleId, icon, children }: ChartCardProps) => {
  const headingId = `${titleId.replace(/\./g, '-')}-heading`;

  return (
    <Paper
      component="section"
      aria-labelledby={headingId}
      elevation={0}
      sx={{ p: { xs: 1.5, md: 2 }, height: '100%', border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ px: 0.5, pt: 0.5, mb: 1 }}>
        <Box sx={{ color: 'primary.main', display: 'grid', placeItems: 'center', mt: 0.25 }}>{icon}</Box>
        <Box>
          <Typography id={headingId} variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            <FormattedMessage id={titleId} />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage id={subtitleId} />
          </Typography>
        </Box>
      </Stack>
      {children}
    </Paper>
  );
};

const ChartLegend = ({ items }: { items: ChartLegendItem[] }) => (
  <Stack
    component="ul"
    direction="row"
    columnGap={2}
    rowGap={0.5}
    flexWrap="wrap"
    sx={{ minHeight: 24, m: 0, px: 1, py: 0.25, listStyle: 'none' }}>
    {items.map((item) => (
      <Stack component="li" key={item.label} direction="row" spacing={0.75} alignItems="center">
        <Box
          aria-hidden="true"
          sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {item.label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

const EmptyChart = () => (
  <Box sx={{ height: 300, display: 'grid', placeItems: 'center', px: 2, textAlign: 'center' }}>
    <Box>
      <QueryStatsRoundedIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 0.75 }} />
      <Typography variant="body2" color="text.secondary">
        <FormattedMessage id="freshdeskFeedback.charts.empty" />
      </Typography>
    </Box>
  </Box>
);

const ChartSkeleton = () => <Skeleton variant="rounded" height={300} sx={{ m: 1 }} />;

const FeedbackCharts = ({ distribution, trend, loading }: FeedbackChartsProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const hasDistribution = distribution.some((point) => point.count > 0);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, lg: 5 }}>
        <ChartCard
          titleId="freshdeskFeedback.charts.distribution.title"
          subtitleId="freshdeskFeedback.charts.distribution.subtitle"
          icon={<BarChartRoundedIcon />}>
          {loading ? (
            <ChartSkeleton />
          ) : hasDistribution ? (
            <>
              <ChartLegend
                items={[
                  {
                    color: theme.palette.primary.main,
                    label: intl.formatMessage({ id: 'freshdeskFeedback.charts.responseCount' }),
                  },
                ]}
              />
              <BarChart
                height={300}
                borderRadius={5}
                grid={{ horizontal: true }}
                xAxis={[
                  {
                    scaleType: 'band',
                    data: distribution.map((point) =>
                      intl.formatMessage({ id: 'freshdeskFeedback.charts.ratingLabel' }, { rate: point.rating })
                    ),
                  },
                ]}
                yAxis={[{ min: 0 }]}
                series={[
                  {
                    data: distribution.map((point) => point.count),
                    label: intl.formatMessage({ id: 'freshdeskFeedback.charts.responseCount' }),
                    color: theme.palette.primary.main,
                  },
                ]}
                margin={{ left: 45, right: 15, top: 12, bottom: 35 }}
                slotProps={{ legend: { hidden: true } }}
              />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 7 }}>
        <ChartCard
          titleId="freshdeskFeedback.charts.trend.title"
          subtitleId="freshdeskFeedback.charts.trend.subtitle"
          icon={<QueryStatsRoundedIcon />}>
          {loading ? (
            <ChartSkeleton />
          ) : trend.length ? (
            <>
              <ChartLegend
                items={[
                  {
                    color: theme.palette.success.main,
                    label: intl.formatMessage({ id: 'freshdeskFeedback.charts.averageRating' }),
                  },
                  {
                    color: theme.palette.primary.main,
                    label: intl.formatMessage({ id: 'freshdeskFeedback.charts.responseCount' }),
                  },
                ]}
              />
              <LineChart
                height={300}
                grid={{ horizontal: true }}
                xAxis={[{ scaleType: 'point', data: trend.map((point) => point.label) }]}
                yAxis={[
                  { id: 'rating', min: 1, max: 5, position: 'left' },
                  { id: 'volume', min: 0, position: 'right', tickMinStep: 1 },
                ]}
                series={[
                  {
                    data: trend.map((point) => point.averageRating),
                    label: intl.formatMessage({ id: 'freshdeskFeedback.charts.averageRating' }),
                    color: theme.palette.success.main,
                    curve: 'linear',
                    yAxisId: 'rating',
                    valueFormatter: (value) =>
                      value === null ? '' : `${intl.formatNumber(value)} / ${MAX_FRESHDESK_RATING}`,
                  },
                  {
                    data: trend.map((point) => point.responseCount),
                    label: intl.formatMessage({ id: 'freshdeskFeedback.charts.responseCount' }),
                    color: theme.palette.primary.main,
                    curve: 'linear',
                    yAxisId: 'volume',
                    showMark: trend.length === 1,
                    valueFormatter: (value) => (value === null ? '' : intl.formatNumber(value)),
                  },
                ]}
                margin={{ left: 45, right: 50, top: 12, bottom: 35 }}
                slotProps={{ legend: { hidden: true } }}
              />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
      </Grid>
    </Grid>
  );
};

export default FeedbackCharts;
